// 詳細內容請參考： apps\taoyuan-health-web\src\app\frontend\careAssessment\README.md

import { AssessmentType } from '@/api/AssessmentPrinting/AssessmentPrintingList';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

type checkboxValue = 0 | 1; // 0: 否, 1: 是

export interface mentalHealthForm {
    name: string;
    identityNumber: string;
    telephone: string;
    birthday: string;
    sex: number; // 1: 男 2: 女
    contactName: string;
    contactTelephone: string;
    relation: string;
    marriageStatus: number; // 1:已婚 2: 喪偶 3:離婚
    livingAddress: string;
    livingStatus: number; // 1:與配偶同住 2:與家人同住（如子女、孫子女 3:獨居 4:安養機構
    bodyStatus: number[]; // 1: 糖尿病 2: 高血壓 3: 心臟病 4: 中風 5: 癌症
    otherInfo: string;
    testLocation: string;
    fillFormDate: string;
    satisfactionLevel: checkboxValue;
    activityInterestReduction: checkboxValue;
    emptinessFeeling: checkboxValue;
    boredomFeeling: checkboxValue;
    mentalWellbeing: checkboxValue;
    fearOfUnfortunateEvents: checkboxValue;
    happinessFeeling: checkboxValue;
    helplessFeeling: checkboxValue;
    homebodyTendency: checkboxValue;
    lifeAppreciation: checkboxValue;
    memoryDifficulty: checkboxValue;
    vitalityFeeling: checkboxValue;
    hopelessFeeling: checkboxValue;
    happinessComparison: checkboxValue;
    dataSource: 'self' | 'other';
    dataSourceOther: string;
    ['dataSource-Comment']: string;
    canReferral: boolean;
    fillFormName: string;
    referralsUser: string;
    fillFormUnitTelephone: string;
}

type radioValue = 0 | 1 | 2; // 0: 無改變, 1: 是, 2: 不知道（不適用）

export interface dementiaCareForm {
    fillingLocation: number; // 1: 衛生所 2: 醫療院所 3: 社區關懷據點 4: 其他
    name: string;
    identityNumber: string;
    gender: number; // 1: 男 2: 女
    birthday: string;
    address: string;
    isAboriginal: boolean;
    telephone: string;
    cellPhone: string;
    educationLevel: number; // 1: 未就學 2:國小畢(肄) 3:國中畢(肄) 4:高中職畢(肄) 5:大學畢(肄)
    hasFilledInLastYear: boolean;
    date: string;
    judgmentDifficulties: radioValue;
    interestInActivities: radioValue;
    repeatingQuestions: radioValue;
    usingToolsDifficulties: radioValue;
    forgettingMonthYear: radioValue;
    handlingComplexFinancialIssues: radioValue;
    rememberingAppointments: radioValue;
    thinkingMemoryIssues: radioValue;
    screeningUnit: string;
    screeningStaff: string;
}

export interface TServiceItemForm {
    formId: number;
    id: number;
    name: string;
    canAdd: boolean;
    canDownload: boolean;
    canFilled: boolean;
    fileUrl: string | null;
    canDelete: boolean;
    type?: AssessmentType;
}

export interface TTempService {
    id: number; // assessment id
    assessmentDate: string;
    assessmentName: string;
    assessmentId: number;
    userId: number;
    userName: string;
    identityNumber: string;
    hospitalId?: number;
    addToAssessmentRecord: boolean;
    forms: TServiceItemForm[];
}

export interface TTempForm {
    // id: number; // assessment id
    assessmentDate: string;
    // assessmentName: string;
    // assessmentId: number;
    userId: number;
    userName: string;
    identityNumber: string;
    hospitalId?: number;
    // addToAssessmentRecord: boolean;
    forms: TServiceItemForm[];
}

export interface TTempFormData extends TServiceItemForm {
    serviceId?: number;
    data?: dementiaCareForm | mentalHealthForm | null;
}

@Injectable({
    providedIn: 'root',
})
export class CareAssessmentDraftService {
    /** 暫存新增時，填寫完的表單的內容 */
    private readonly tempServices = new BehaviorSubject<TTempService[] | null>(null);
    private readonly tempForms = new BehaviorSubject<TTempForm[] | null>(null);
    private readonly tempFormData = new BehaviorSubject<TTempFormData[] | null>(null);

    // for debug
    getData(): { services: TTempService[], forms: TTempForm[], formData: TTempFormData[] } {
        return ({
            services: this.tempServices.getValue() ?? [],
            forms: this.tempForms.getValue() ?? [],
            formData: this.tempFormData.getValue() ?? [],
        });
    }

    // 依照assessmentId，取得暫存的綜合評估服務
    getTempService(id: number): TTempService | null {
        const current = this.tempServices.getValue() ?? [];
        return current.find((s) => s.id === id) ?? null;
    }

    // 設定暫存的綜合評估服務
    setTempService(service: TTempService) {
        const current = this.tempServices.getValue() ?? [];
        const index = current.findIndex((s) => s.id === service.id);
        if (index > -1) {
            current[index] = service;
        }
        else {
            current.push(service);
        }
        this.tempServices.next(current);
    }

    // 取得暫存的個案評估單紀錄
    getTempForm(): TTempForm | null {
        const current = this.tempForms.getValue() ?? [];
        return current.length > 0 ? current[0] : null;
    }

    // 設定暫存的個案評估單紀錄，因畫面設計，只需暫存一筆
    setTempForm(form: TTempForm): void {
        this.tempForms.next([form]);
    }

    // 依照表單id，取得暫存的表單資料
    getTempFormData(id: number): TTempFormData | null {
        const current = this.tempFormData.getValue() ?? [];
        return current.find((f) => f.id === id) ?? null;
    }

    // 設定暫存的表單資料
    setTempFormData(form: TTempFormData): void {
        const current = this.tempFormData.getValue() ?? [];
        const index = current.findIndex((f) => f.id === form.id);
        if (index > -1) {
            current[index] = form;
        }
        else {
            current.push({ data: null, ...form });
        }
        this.tempFormData.next(current);
    }

    // 依照表單id，重設暫存的表單資料
    resetTempFormData(id: number): void {
        const current = this.tempFormData.getValue() ?? [];
        const index = current.findIndex((f) => f.id === id);
        if (index > -1) {
            current.splice(index, 1);
            this.tempFormData.next(current);
        }
    }

    // 重設所有暫存資料
    resetTemp(): void {
        this.tempServices.next(null);
        this.tempForms.next(null);
        this.tempFormData.next(null);
    }
}
